import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { of } from "rxjs";
import { catchError, concatMap, filter, finalize, map, takeUntil } from "rxjs/operators";

import { SvcNowFuncTab } from "./svcnow.data";
import { TopMenuService } from "../../../../../app/content/menu.service";
import { NAService } from "../../../../../app/API/na.service";
import { AccountService } from "../../../../../app/entry/account.service";
import { IAPIRx } from "../../../../../app/API/api.base";
import { IPlugin, IPluginInstance } from "../../../../../app/API/v1/Plugin/api.plugin.data";
import { WorkplacePluginService } from "../plugin.service";
import { Logger } from "../../../../../app/lib/common/logger";
import { AutoUnsubscribeComponent } from "../../../../../app/content/virtual/auto-unsubscribe.component";

@Component({
    templateUrl: './svcnow-wrapper.component.html',
    standalone: false
})
export class ServiceNowWrapperComponent extends AutoUnsubscribeComponent implements OnInit {
    readonly ROUTER_LINK_REGISTER: string = 'register';
    readonly ROUTER_LINK_PAIRING: string = 'pairing';
    readonly ROUTER_LINK_CALENDAR_CONFIG: string = 'calendar-config';

    _loading: boolean = false;
    _activeTab: SvcNowFuncTab = SvcNowFuncTab.Register;
    _funcs: { type: SvcNowFuncTab, link: string, displayName: string }[] = [];
    _errorMessage: string;
    _pluginName: string;

    constructor(private router: Router, private route: ActivatedRoute, private menuSvc: TopMenuService, private naSvc: NAService, private accountSvc: AccountService, private pluginSvc: WorkplacePluginService) {
        super();
    }

    ngOnInit(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribe$) // 避免 memory leak
        ).subscribe(() => {
            if (!this.route.firstChild) {
                this._activeTab = SvcNowFuncTab.Register;
            }
            if (this._activeTab === SvcNowFuncTab.Register) {
                this.handleRouteLogic();
            }
        });

        this.handleRouteLogic();
        /*
        this.naSvc.runPluginHealthCheck('c8d8c1e0-aeed-4a46-b0c4-a3b7b6a5a4e4', this.accountSvc.token).subscribe(res => {
            console.log('------------ run plugin check: ', res);
        });
        */
        /*
        this.naSvc.removePluginInstance("b02065b8-a42c-4695-8210-239edd06667f", this.accountSvc.token).subscribe(res => {
            console.log('--------- remove plugin instance: ', res);
        });
        */
    }

    private handleRouteLogic(): void {
        if (this._loading) {
            return;
        }

        this._pluginName = this.getParamFromRouteTree(this.router.routerState.root, 'brand');
        this._funcs = [
            { type: SvcNowFuncTab.Register, link: this.menuSvc.get3rdAppRouter(this._pluginName, this.ROUTER_LINK_REGISTER), displayName: 'Registration' },
            { type: SvcNowFuncTab.RoomMapping, link: this.menuSvc.get3rdAppRouter(this._pluginName, this.ROUTER_LINK_PAIRING), displayName: 'Device - Space configuration' },
            { type: SvcNowFuncTab.CalendarConfig, link: this.menuSvc.get3rdAppRouter(this._pluginName, this.ROUTER_LINK_CALENDAR_CONFIG), displayName: 'Booking software configuration' }
        ];

        this._loading = true;
        this.naSvc.listAvailablePluginTypes(this.accountSvc.token).pipe(
            concatMap((res: IAPIRx<{ itemList: IPlugin[], limit: number, skip: number, total: number }>) => {
                if (res.error != 0) {
                    throw { errorMessage: 'Failed to list plugins: ' + res.errorMessage };
                }

                const pluginShowNameToCompare: string = this._pluginName === 'svcnow' ? 'ServiceNow' : (this._pluginName === 'ems' ? 'ACCRUENT EMS' : '');
                const svcnowPlugin: IPlugin = res.data.itemList.find(p => p.pluginShowName.indexOf(pluginShowNameToCompare) >= 0);
                if (!svcnowPlugin) {
                    throw { errorMessage: `No ${pluginShowNameToCompare} plugin was found` };
                }

                return this.naSvc.listPluginInstances(this.accountSvc.token).pipe(
                    concatMap((res: IAPIRx<{ itemList: IPluginInstance[], limit: number, skip: number, total: number }>) => {
                        if (res.error != 0) {
                            throw { errorMessage: 'Failed to list plugin instances: ' + res.errorMessage };
                        }

                        const svcnowPluginInstance: IPluginInstance = res.data.itemList.find(pInst => pInst.pluginInstanceOwnerID === this.accountSvc.enterpriseAccountID && pInst.pluginID === svcnowPlugin.pluginID);
                        if (!svcnowPluginInstance) {
                            // create one servicenow plugin instance
                            return this.naSvc.createPluginInstance(
                                svcnowPlugin.pluginID,
                                {
                                    credential: '1234',
                                    callbackUrl: `${this.pluginSvc.getPluginBaseUrl('')}/plugin/event`, //'https://servicenow.plugins.oniadea.com/workplace/plugin/event',
                                    remark: `Created by IAdeaCare enterprise: ${this.accountSvc.enterpriseAccountID}`
                                },
                                ['EVENT_VIRTUALDEVICE_UNPAIR'],
                                this.accountSvc.token
                            ).pipe(
                                map((res: IAPIRx<IPluginInstance>) => {
                                    Logger.logInfo('[svcnow][wrapper]', 'onInit', `Create new plugin instance: `, res);
                                    if (res.error != 0) {
                                        throw { errorMessage: 'Failed to create plugin instance: ' + res.errorMessage };
                                    }

                                    return { data: res.data };
                                })
                            )
                        }

                        Logger.logInfo('svcnow-wrapper', 'onInit', `Use existing plugin instance: ${svcnowPluginInstance.pluginInstanceID}`);
                        return of({ data: svcnowPluginInstance });
                    }),
                );
            }),
            catchError((err: { errorMessage: string }) => of(err)),
            finalize(() => this._loading = false)
        ).subscribe((res: { data?: IPluginInstance, errorMessage?: string }) => {
            if (!res.errorMessage && res.data) {
                this.pluginSvc.savePluginInstance(res.data);
                this.router.navigate([this.menuSvc.get3rdAppRouter(this._pluginName, this.ROUTER_LINK_REGISTER)]);
            }

            this._errorMessage = res.errorMessage;
        });
    }

    private getParamFromRouteTree(route: ActivatedRoute, paramName: string): string | null {
        if (route.snapshot.paramMap.has(paramName)) {
            return route.snapshot.paramMap.get(paramName);
        }
        for (const child of route.children) {
            const param = this.getParamFromRouteTree(child, paramName);
            if (param) return param;
        }
        return null;
    }
}